import { useState, useCallback, useEffect } from 'react';
import { Image, RefreshCw, Download, Monitor, Smartphone, Square, AlertTriangle } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import type { StrategicDirection } from '../../lib/strategicDirections';

interface AdResult {
 image: string;
 mimeType: string;
 format: string;
}

interface Props {
 headline: string;
 subheadline?: string;
 cta: string;
 price?: string;
 packageName: string;
 direction: StrategicDirection;
 savedAds?: Record<string, AdResult>;
 onAdsChanged?: (ads: Record<string, AdResult>) => void;
}

const AD_FORMATS = [
 { key: 'feed', label: 'Social Feed', icon: <Square size={14} />, desc: '1080x1080' },
 { key: 'story', label: 'Story / Vertical', icon: <Smartphone size={14} />, desc: '1080x1920' },
 { key: 'banner', label: 'Display Banner', icon: <Monitor size={14} />, desc: '1200x628' },
] as const;

function downloadImage(base64: string, mimeType: string, filename: string) {
 const byteChars = atob(base64);
 const byteNumbers = new Array(byteChars.length);
 for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
 const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
 const url = URL.createObjectURL(blob);
 const a = document.createElement('a');
 a.href = url;
 a.download = filename;
 a.click();
 URL.revokeObjectURL(url);
}

export function AdCreativePreview({ headline, subheadline, cta, price, packageName, direction, savedAds, onAdsChanged }: Props) {
 const [ads, setAds] = useState<Record<string, AdResult>>(savedAds ?? {});
 const [loading, setLoading] = useState<Record<string, boolean>>({});
 const [errors, setErrors] = useState<Record<string, string>>({});

 useEffect(() => {
 if (savedAds && Object.keys(savedAds).length > 0) {
 setAds(savedAds);
 }
 }, [savedAds]);

 const generateAd = useCallback(async (format: string) => {
 setLoading(prev => ({ ...prev, [format]: true }));
 setErrors(prev => ({ ...prev, [format]: '' }));
 try {
 const res = await apiFetch('/api/generate-ad', {
 method: 'POST',
 body: JSON.stringify({
 headline, subheadline, cta, price, packageName,
 directionName: direction.name, tone: direction.tone, format,
 }),
 });
 if (!res.ok) {
 const err = await res.json().catch(() => ({ error: 'Failed' }));
 throw new Error(err.error || `Error ${res.status}`);
 }
 const data: AdResult = await res.json();
 const updated = { ...ads, [format]: data };
 setAds(updated);
 onAdsChanged?.(updated);
 } catch (e) {
 setErrors(prev => ({ ...prev, [format]: e instanceof Error ? e.message : 'Failed to generate' }));
 } finally {
 setLoading(prev => ({ ...prev, [format]: false }));
 }
 }, [headline, subheadline, cta, price, packageName, direction, ads, onAdsChanged]);

 const generateAll = useCallback(async () => {
 let current = { ...ads };
 for (const fmt of AD_FORMATS) {
 setLoading(prev => ({ ...prev, [fmt.key]: true }));
 setErrors(prev => ({ ...prev, [fmt.key]: '' }));
 try {
 const res = await apiFetch('/api/generate-ad', {
 method: 'POST',
 body: JSON.stringify({
 headline, subheadline, cta, price, packageName,
 directionName: direction.name, tone: direction.tone, format: fmt.key,
 }),
 });
 if (!res.ok) throw new Error(`Error ${res.status}`);
 const data: AdResult = await res.json();
 current = { ...current, [fmt.key]: data };
 setAds(current);
 } catch (e) {
 setErrors(prev => ({ ...prev, [fmt.key]: e instanceof Error ? e.message : 'Failed' }));
 } finally {
 setLoading(prev => ({ ...prev, [fmt.key]: false }));
 }
 }
 onAdsChanged?.(current);
 }, [headline, subheadline, cta, price, packageName, direction, ads, onAdsChanged]);

 const hasAny = Object.keys(ads).length > 0;
 const anyLoading = Object.values(loading).some(Boolean);

 return (
 <div className="rounded-xl border border-chart-amber/30 bg-chart-amber/5 overflow-hidden">
 <div className="px-5 py-3.5 flex items-center justify-between">
 <div className="flex items-center gap-2.5">
 <Image size={15} className="text-orange-500" />
 <h3 className="text-xs font-bold uppercase tracking-wider text-primary">Ad Creative Previews</h3>
 </div>
 <div className="flex items-center gap-2">
 {hasAny && (
 <button onClick={generateAll} disabled={anyLoading}
 className="flex items-center gap-1.5 px-3 py-1.5 text-micro font-medium rounded-lg border border-border text-secondary hover:text-primary hover:bg-surface transition-colors disabled:opacity-50">
 <RefreshCw size={10} className={anyLoading ? 'animate-spin' : ''} />
 Regenerate All
 </button>
 )}
 {!hasAny && (
 <button onClick={generateAll} disabled={anyLoading}
 className="flex items-center gap-1.5 px-3 py-1.5 text-micro font-medium rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors disabled:opacity-50">
 {anyLoading ? <RefreshCw size={10} className="animate-spin" /> : <Image size={10} />}
 {anyLoading ? 'Generating...' : 'Generate Ad Previews'}
 </button>
 )}
 </div>
 </div>

 <div className="px-5 pb-5">
 {!hasAny && !anyLoading && (
 <p className="text-detail text-muted mb-3">
 Generate AI-powered ad creative mockups using your headline, direction tone, and brand colors. Powered by Gemini AI.
 </p>
 )}

 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 {AD_FORMATS.map(fmt => {
 const ad = ads[fmt.key];
 const isLoading = loading[fmt.key];
 const error = errors[fmt.key];
 return (
 <div key={fmt.key} className="rounded-lg border border-border/50 overflow-hidden" style={{ backgroundColor: 'var(--color-card)' }}>
 <div className="px-3 py-2 border-b border-border/50 flex items-center justify-between">
 <div className="flex items-center gap-1.5">
 <span className="text-muted">{fmt.icon}</span>
 <span className="text-micro font-semibold text-secondary">{fmt.label}</span>
 <span className="text-micro text-muted">({fmt.desc})</span>
 </div>
 {!ad && !isLoading && (
 <button onClick={() => generateAd(fmt.key)} className="text-micro text-orange-500 hover:text-orange-600 font-medium">Generate</button>
 )}
 {ad && (
 <div className="flex items-center gap-1">
 <button onClick={() => downloadImage(ad.image, ad.mimeType, `AlterMe-${fmt.key}-${direction.name.replace(/\s+/g, '-')}.png`)}
 className="p-1 text-muted hover:text-primary transition-colors" title="Download">
 <Download size={11} />
 </button>
 <button onClick={() => generateAd(fmt.key)} disabled={isLoading}
 className="p-1 text-muted hover:text-primary transition-colors" title="Regenerate">
 <RefreshCw size={11} className={isLoading ? 'animate-spin' : ''} />
 </button>
 </div>
 )}
 </div>
 <div className="p-3">
 {isLoading && (
 <div className="flex flex-col items-center justify-center py-8">
 <RefreshCw size={16} className="animate-spin text-orange-400 mb-2" />
 <span className="text-micro text-muted">Generating {fmt.label}...</span>
 </div>
 )}
 {error && !isLoading && (
 <div className="flex items-center gap-2 py-4 px-2 text-micro text-red-500">
 <AlertTriangle size={12} />
 <span className="flex-1">{error}</span>
 <button onClick={() => generateAd(fmt.key)} className="underline">Retry</button>
 </div>
 )}
 {ad && !isLoading && (
 <img src={`data:${ad.mimeType};base64,${ad.image}`} alt={`${fmt.label} preview`} className="w-full rounded-md shadow-sm" />
 )}
 {!ad && !isLoading && !error && (
 <div className="flex flex-col items-center justify-center py-8 text-muted">
 <Image size={20} className="mb-1 opacity-30" />
 <span className="text-micro">Click Generate to preview</span>
 </div>
 )}
 </div>
 </div>
 );
 })}
 </div>
 <p className="text-micro text-muted italic mt-3">
 Ad previews generated by Gemini AI. These are concept mockups for internal review — not production-ready assets.
 </p>
 </div>
 </div>
 );
}
